import { Router, NavigationEnd } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { MenuItem } from './menu.model';
import { Component, OnInit, ViewChild, ElementRef, Inject, Renderer2, AfterViewInit } from '@angular/core';
import { MENU, MENU_AUTH } from './menu';
import { Authentication } from 'src/app/helpers/authentication';
import { KanbanApiService } from 'src/app/modules/kanban/services/kanban-api.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { AuthStateService } from 'src/app/core/services/auth-state.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, AfterViewInit {

  @ViewChild('sidebarMenu') sidebarMenu: ElementRef;

  private readonly DETAIL_ROUTES = ['/home', '/registerTime', '/proyectDetails', '/kanban/project', '/ofertDetails', '/ofertForm', '/proyectForm', '/capacity-planning'];
  public isFolded = false;
  private hoverLeaveTimeout: any;
  private isFirstRender = true;

  public auth: Authentication = new Authentication();

  constructor(@Inject(DOCUMENT) private readonly document: Document, private readonly renderer: Renderer2, private readonly router: Router, private readonly kanbanApi: KanbanApiService, public themeService: ThemeService, private readonly authState: AuthStateService) {
  }

  ngOnInit(): void {
    this.subscribeToRouterEvents();

    if (this.auth.isLogin()) {
      this.authState.menuItems = MENU_AUTH;
      this.loadDynamicMenuItems();
    } else {
      this.authState.menuItems = MENU;
    }

    /**
     * Sidebar-folded on desktop (min-width:992px and max-width: 1199px)
     */
    const desktopMedium = globalThis.matchMedia('(min-width:992px) and (max-width: 1199px)');
    desktopMedium.addEventListener('change', () => {
      this.iconSidebar(desktopMedium);
    });
    this.iconSidebar(desktopMedium);
  }

  public getMenu(): MenuItem[] {
    this.authState.userDataAuth = this.auth.getUserDataAuth();
    return this.authState.menuItems;
  }

  ngAfterViewInit() {
    if (this.auth.isLogin()) {
      this._activateMenuDropdown();
      setTimeout(() => { this.isFirstRender = false; }, 500);
    }
  }

  private subscribeToRouterEvents(): void {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();

        // Mobile: close sidebar on navigation
        if (globalThis.matchMedia('(max-width: 991px)').matches) {
          this.document.body.classList.remove('sidebar-open');
        }

        // Desktop: bidirectional collapse/expand
        if (globalThis.matchMedia('(min-width: 992px)').matches) {
          const url = event.urlAfterRedirects || event.url || '';
          const isDetailRoute = this.DETAIL_ROUTES.some(r => url.includes(r));
          if (isDetailRoute) {
            this.collapseSidebar();
          } else {
            this.expandSidebar();
          }
        }

        // Refrescar siempre el menú dinámico de "Mis Tableros" después de cada navegación,
        // para que los cambios de asignación de proyectos (alta/baja) se reflejen sin recargar la página.
        if (this.auth.isLogin()) {
          this.loadDynamicMenuItems();
        }
      }
    });
  }

  /**
   * Toggle sidebar on hamburger button click
   */
  toggleSidebar(e: Event) {
    e.preventDefault();
    if (globalThis.matchMedia('(min-width: 992px)').matches) {
      this.document.body.classList.toggle('sidebar-folded');
      this.isFolded = this.document.body.classList.contains('sidebar-folded');
    } else {
      this.document.body.classList.toggle('sidebar-open');
    }
  }

  /**
   * Open sidebar when hover (in folded state)
   */
  operSidebarFolded() {
    clearTimeout(this.hoverLeaveTimeout);
    if (this.document.body.classList.contains('sidebar-folded')) {
      this.document.body.classList.add('open-sidebar-folded');
      const sidebar = this.document.querySelector('.sidebar') as HTMLElement;
      sidebar?.addEventListener('transitionend', () => {
        const ps = this.document.querySelector('.sidebar-body .ps');
        if (ps) (ps as any).dispatchEvent(new Event('ps-update'));
      }, { once: true });
    }
  }

  /**
   * Fold sidebar after mouse leave (in folded state)
   */
  closeSidebarFolded() {
    if (this.document.body.classList.contains('sidebar-folded')) {
      clearTimeout(this.hoverLeaveTimeout);
      this.hoverLeaveTimeout = setTimeout(() => {
        this.document.body.classList.remove('open-sidebar-folded');
      }, 150);
    }
  }

  private collapseSidebar(): void {
    this.document.body.classList.add('sidebar-folded');
    // Asegurar que, si el sidebar estaba expandido por hover (open-sidebar-folded),
    // se cierre completamente al navegar a una vista de detalle (crear/agregar tratos, proyectos, etc.).
    this.document.body.classList.remove('open-sidebar-folded');
    this.isFolded = true;
  }

  private expandSidebar(): void {
    const desktopMedium = globalThis.matchMedia('(min-width:992px) and (max-width: 1199px)');
    if (desktopMedium.matches) return;
    this.document.body.classList.remove('sidebar-folded');
    this.isFolded = false;
  }

  getStaggerDelay(index: number): number {
    if (!this.isFirstRender) return 0;
    return Math.min(index * 30, 300);
  }

  public isConteinsRoles(): boolean {
    return this.authState.isConteinsRoles;
  }

  /**
   * Returns true if the section header at index i has at least one visible item after it
   */
  hasSectionVisibleItems(index: number): boolean {
    const menu = this.getMenu();
    for (let j = index + 1; j < menu.length; j++) {
      const next = menu[j];
      if (next.isTitle) break; // reached next section, no visible items found
      if (this.hasItems(next) || this.hasItemPermiso(next)) return true;
    }
    return false;
  }

  /**
   * Sidebar-folded on desktop (min-width:992px and max-width: 1199px)
   */
  iconSidebar(mq: MediaQueryList) {
    const isDetailRoute = this.DETAIL_ROUTES.some(r => globalThis.location.hash.includes(r));
    if (isDetailRoute) {
      this.document.body.classList.add('sidebar-folded');
      this.isFolded = true;
      return;
    }
    if (mq.matches) {
      this.document.body.classList.add('sidebar-folded');
      this.isFolded = true;
    } else {
      this.document.body.classList.remove('sidebar-folded');
      this.isFolded = false;
    }
  }

  private loadDynamicMenuItems(): void {
    const dynamicItem = this.authState.menuItems.find(item => item.dynamic);
    if (!dynamicItem) return;

    this.kanbanApi.getMyProjects().subscribe({
      next: (projects) => {
        const MAX_ITEMS = 10;
        const subItems: MenuItem[] = projects.slice(0, MAX_ITEMS).map(p => ({
          label: this.normalizeProjectName(p.nombre),
          link: '/kanban/project/' + p.proyectoId
        }));

        subItems.push({
          label: 'Ver todos →',
          link: '/kanban'
        });

        dynamicItem.subItems = subItems;

        setTimeout(() => {
          this._activateMenuDropdown();
          this.expandDynamicSubmenu();
        }, 100);
      },
      error: () => {
        dynamicItem.subItems = [];
      }
    });
  }

  /**
   * Expande el submenu dinámico (Mis Tableros) por defecto
   */
  private expandDynamicSubmenu(): void {
    const subMenus = this.document.querySelectorAll('.sidebar-nav .sub-menu');
    subMenus.forEach(subMenu => {
      subMenu.classList.add('mm-show');
      subMenu.parentElement?.classList.add('mm-active');
    });
  }

  toggleSubMenu(event: Event): void {
    const link = event.currentTarget as HTMLElement;
    const li = link.parentElement;
    if (!li) return;
    const subMenu = li.querySelector('.sub-menu') as HTMLElement;
    if (!subMenu) return;
    subMenu.classList.toggle('mm-show');
    li.classList.toggle('mm-active');
  }

  private normalizeProjectName(name: string): string {
    return name.split(/(\s+)/).map(word => {
      const trimmed = word.trim();
      if (!trimmed) return word;
      // Preserve codes containing digits (OP3903113, prueba2, 40)
      if (/\d/.test(trimmed)) return trimmed;
      // Preserve short acronyms (2-4 chars all caps: BPM, MVP, EX)
      if (/^[A-Z]{2,4}$/.test(trimmed)) return trimmed;
      // Title case the rest
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    }).join('');
  }

  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems === undefined ? false : item.subItems.length > 0;
  }


  /**
   *
   * Return true or false if given menu item has permission or not
   * this method is used for menu item permission
   */
  hasItemPermiso(item: MenuItem) {
    let roles: string[] = this.authState.userDataAuth.auth;
    for (let m of MENU_AUTH) {
      if (m.label == item.label) {
        if (m.role) {
          let rol = roles.filter(r => m.role?.includes(r));
          return rol.length > 0;
        } else {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Reset the menus then hilight current active menu item
   */
  _activateMenuDropdown() {
    this.resetMenuItems();
    this.activateMenuItems();
  }


  /**
   * Resets the menus
   */
  resetMenuItems() {

    const links: HTMLCollectionOf<Element> = document.getElementsByClassName('nav-link-ref');
    Array.from(links).forEach(i => {
      this.deleteClassMenuItems(i);
    });
  };

  private deleteClassMenuItems(menuItemEl: any): void {
    menuItemEl.classList.remove('mm-active');
    const parentEl = menuItemEl.parentElement;

    if (parentEl) {
      parentEl.classList.remove('mm-active');
      const parent2El = parentEl.parentElement;

      if (parent2El) {
        parent2El.classList.remove('mm-show');
      }

      const parent3El = parent2El?.parentElement;
      if (parent3El) {
        parent3El.classList.remove('mm-active');

        if (parent3El.classList.contains('side-nav-item')) {

          this.deleteClassLinkSideNav(parent3El);

        }

        const parent4El = parent3El.parentElement;
        if (parent4El) {
          parent4El.classList.remove('mm-show');

          const parent5El = parent4El.parentElement;
          if (parent5El) {
            parent5El.classList.remove('mm-active');
          }
        }
      }
    }
  }

  private deleteClassLinkSideNav(parent3El: any): void {
    const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');
    if (firstAnchor) {
      firstAnchor.classList.remove('mm-active');
    }
  }

  /**
   * Toggles the menu items
   */
  activateMenuItems() {

    const links: HTMLCollectionOf<any> = document.getElementsByClassName('nav-link-ref');

    let menuItemEl: any = null;

    Array.from(links).forEach(link => {
      if (globalThis.location.hash === link['hash']) {
        menuItemEl = link;
      }
    });
    this.addClassMenuItems(menuItemEl);

    // If no exact match found, try route-based matching for parent items
    if (!menuItemEl) {
      const currentHash = globalThis.location.hash;
      for (const item of this.authState.menuItems) {
        if (item.routes && item.subItems?.length > 0) {
          const matchesRoute = item.routes.some(route => currentHash.includes(route));
          if (matchesRoute) {
            Array.from(links).forEach(link => {
              if ((link as HTMLElement).getAttribute('href') === '#' + item.link ||
                  link['hash'] === '#' + item.link) {
                this.addClassMenuItems(link);
              }
            });
            break;
          }
        }
      }
    }
  };

  private addClassMenuItems(menuItemEl: any): void {
    if (menuItemEl) {
      menuItemEl.classList.add('mm-active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');

        const parent2El = parentEl.parentElement;
        if (parent2El) {
          parent2El.classList.add('mm-show');
        }

        const parent3El = parent2El.parentElement;
        if (parent3El) {
          parent3El.classList.add('mm-active');

          this.addClassSideNavItem(parent3El);

          this.addClassParent4El(parent3El.parentElement);
        }
      }
    }
  }

  private addClassParent4El(parent4El: any): void {
    if (parent4El) {
      parent4El.classList.add('mm-show');

      const parent5El = parent4El.parentElement;
      if (parent5El) {
        parent5El.classList.add('mm-active');
      }
    }
  }

  private addClassSideNavItem(parent3El: any): void {
    if (parent3El.classList.contains('side-nav-item')) {
      const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');

      if (firstAnchor) {
        firstAnchor.classList.add('mm-active');
      }
    }
  }

}
